<template>
    <div class="dropdown-categorized">
        <template v-if="usePopover">
            <template v-if="triggerType === 'icon'">
                <ButtonIcon ref="trigger"
                    :icon="icon"
                    :title="btnTitle"
                    :class="['el--t-' + btnType, 'el--s-' + btnSize]"
                    :disabled="disabled"
                    @click.prevent.stop="onToggle" />
            </template>
            <template v-else-if="triggerType === 'select'">
                <button ref="trigger"
                    type="button"
                    class="f fh fa-c category-select-trigger dw-10"
                    :class="['el--t-' + btnType, 'el--s-' + btnSize]"
                    :disabled="disabled"
                    :title="btnTitle"
                    @click.prevent.stop="onToggle">
                    <span class="dropdown-label">{{ selectedItem | athT }}</span>
                    <span class="dropdown-placeholder" v-if="!selectedItem && placeholder">{{ placeholder | athT }}</span>
                    <span class="fg"></span>
                    <i class="sm-l-s sm-r-xs lnr-chevron-down"></i>
                </button>
            </template>
        </template>
        <Dropdown v-else
            uiBtnDisplay="select"
            :uiBtnSize="btnSize"
            :uiBtnType="btnType"
            :pubkey="uid"
            :uiAdjustable="!disabled"
            uiIsComponent
            :uiMaxHeightRem="45">
            <template #btn-content>
                <span v-if="selectedItem">{{ selectedItem | athT }}</span>
                <span class="dropdown-placeholder" v-else-if="placeholder">{{ placeholder | athT }}</span>
            </template>
            <DropdownCategorizedContent :pubkey="uid"
                :value="selectedItem"
                :searchPlaceholder="searchPlaceholder"
                :options="options"
                :extraOptions="extraOptions"
                @item-selected="onItemSelected" />
        </Dropdown>
    </div>
</template>
<script>
import { guuid } from '@aretheyhappy/utils';
import ButtonIcon from '../../atoms/ButtonIcon.vue';
import Dropdown from '../Dropdown.vue';
import DropdownCategorizedPopover from './DropdownCategorizedPopover.vue';
import DropdownCategorizedContent from './DropdownCategorizedContent.vue';

/**
 * Renders a dropdown with categories and search.
 *
 * options must be the following format.
 *
 * [{
 *   category: 'string',
 *   items: [{
 *     value: value for the option
 *     label: localization key or label to display
 *     subtitle: Optional subtitle for the item
 *   }]
 * }]
 */
export default {
    name: 'mead-dropdown-categorized',
    components: {
        Dropdown,
        DropdownCategorizedContent,
        ButtonIcon,
    },
    props: {
        /**
         * The main categorized options rendered in the dropdown, grouped by category. Each entry
         * must be `{category: 'string', items: [{value, label, subtitle?}]}`.
         */
        options: { type: Array, required: true },
        /**
         * Extra options that appear at the top of the dropdown, above the categorized options.
         */
        extraOptions: { type: Array, default: () => [] },
        /**
         * Initial selected value.
         */
        value: { type: String, default: null },
        /**
         * Placeholder text to be shown when there is nothing selected.
         */
        placeholder: { type: String, default: null },
        /**
         * Override for the search input's placeholder text.
         */
        searchPlaceholder: { type: String, default: 'general.search_ellipses' },
        /**
         * Type of trigger button to render when usePopover is true ('select', 'icon', etc.).
         */
        triggerType: { type: String, default: () => 'select' },
        /**
         * Icon to use when triggerType is 'icon'.
         */
        icon: { type: String, default: 'plus' },
        /**
         * Style variant for the trigger button.
         */
        btnType: { type: String, default: 'default' },
        /**
         * Size of the trigger button.
         */
        btnSize: { type: String, default: 'm' },
        /**
         * Whether the component should be in an enabled or disabled state.
         */
        disabled: { type: Boolean, default: false },
        /**
         * Title/tooltip attribute rendered on the trigger button (icon or select
         * variant). Pass false to omit the title attribute.
         */
        btnTitle: {type: [String, Boolean], default: false},
        /**
         * Whether to use a popover or an inline dropdown to render the options (true for
         * backwards compatibility).
         */
        usePopover: { type: Boolean, default: true },
    },
    data() {
        return {
            uid: guuid(),
            // Internal selected value
            selectedItem: this.value || null,
            popover_open: false,
        };
    },
    methods: {
        onToggle: function (event) {
            this.popover_open = true;
            const config = new MeadCommon.PopoverConfig()
                .setComponent(DropdownCategorizedPopover)
                .setMovable(true)
                .setNoPad(true)
                .setMobileFull(true)
                .setAbsolute(false)
                .setEvent(event)
                .setNext(() => {
                    this.popover_open = false;
                })
                .setProps({
                    pubkey: this.uid,
                    value: this.selectedItem,
                    searchPlaceholder: this.searchPlaceholder,
                    options: this.options,
                    extraOptions: this.extraOptions,
                });
            MeadCommon.PopoverService.open(config);
        },
        onItemSelected(item) {
            this.selectedItem = item.label;
            this.$emit('input', item.value, item);
            if (this.usePopover && this.popover_open) {
                MeadCommon.PopoverService.close();
            } else {
                MeadCommon.Flash.publish(`${this.uid}:close`);
            }
        },
    },
    watch: {
        value: function () {
            this.selectedItem = this.value;
        },
    },
    created: function () {
        MeadCommon.Flash.subscribe(this.uid, `${this.uid}:item_selected`, this.onItemSelected);
    },
    destroyed () {
        if (this.popover_open)
            MeadCommon.PopoverService.close();
        MeadCommon.Flash.unsubscribe(this.uid);
    },
};
</script>
<style lang="scss" scoped>
@import '../../../../sass/variables';

.dropdown-categorized {
    .dropdown-placeholder {
        opacity: .5;
    }

    .category-select-trigger {
        background: var(--c-brand-light-bg);
        color: var(--c-brand-light-fg);
        border: none;
        border-radius: $br-m;
        padding: 0 $s-s;
        font-size: $fs-m;
        font-weight: 400;
        font-family: $ff-b;
        cursor: pointer;

        &.el--s-s {
            height: 3rem;
            line-height: 1;
            font-size: $fs-s;
        }

        &.el--s-m {
            height: 4rem;
            line-height: 1;
        }

        &:hover,
        &:active {
            outline: none;
            background: var(--c-brand-default-bg);
        }
    }
}
</style>
